/**
 * PaneReorder — a small toolbar at the top of each pane, revealed on hover.
 *
 * The direct-manipulation half of pane reordering; the other half is the grip on
 * a row in the indicator picker. This one exists because restacking is the kind
 * of thing you want to do while looking at the chart rather than at a list of
 * it, and because it reaches every pane without the list having to.
 *
 * **Hover the pane, not the button.** A control that appears only under the
 * pointer disappears as you move toward it, which is why the value legend's
 * block is always on screen and only its buttons are hover-gated. The same
 * shape here: the whole pane is the hover region, and the toolbar fades in
 * anywhere inside it — but it appears at the top of the band rather than the
 * middle, which is where the price action is.
 *
 * **One segmented group, not two floating buttons.** Two buttons with their own
 * borders read as two unrelated things that happen to be near each other. A
 * single bordered strip with a hairline between the halves reads as one control
 * with two of them, which is what it is. An arrow with nowhere to go is
 * **disabled rather than removed**, so the strip keeps its width instead of
 * shuffling sideways at the ends of the stack — `FloatingButton`'s rule in
 * `ChartGroupView`, for the same reason.
 *
 * Geometry comes from `stackFractions` — the same function the renderer
 * positions SubCharts with and `PaneDividers` takes its seams from — so the
 * toolbar cannot drift off the pane it belongs to when a divider moves.
 * Percentages throughout; the only pixels are the buttons' own.
 *
 * **The gotcha this shares with `PaneDividers`:** SciChart's `ZoomPanModifier`
 * listens on the canvas underneath, so a press that reaches it pans the chart.
 * Every button stops propagation on `pointerdown` as well as on `click`.
 *
 * **And the one that is this component's own:** the hover is *not* detected
 * here. A band `div` per pane with `pointerEvents: "auto"` is the obvious way to
 * know which pane the pointer is over, and it covers the entire chart — so the
 * canvas beneath it never receives another pointer event and the chart cannot be
 * panned or zoomed at all. The overlay is therefore inert end to end, and
 * `ChartGroupView` answers the question instead, off a `pointermove` that
 * bubbles up from the canvas to the container both of them sit in.
 */

import { useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from "react";
import type { Pane } from "@qufolio/model";
import { paneWeights, stackFractions } from "@qufolio/chart-engine";

export interface PaneReorderProps {
  panes: readonly Pane[];
  /**
   * The pane the pointer is over, decided by the container — see the header.
   * `null` while the pointer is off the chart, which hides every toolbar.
   */
  hovered: string | null;
  /** One slot, in the direction given. `-1` is toward the top of the stack. */
  onMove: (paneId: string, delta: -1 | 1) => void;
}

export function PaneReorder({ panes, hovered, onMove }: PaneReorderProps) {
  // Nothing to reorder, and a lone pane should not carry furniture that could
  // only ever be disabled.
  if (panes.length < 2) return null;

  const bands = stackFractions(paneWeights(panes));

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        // The bands span the whole chart, so without this the overlay would
        // swallow every pan and zoom. Only the buttons opt back in.
        pointerEvents: "none",
        // Under `PaneDividers` (6): a seam handle sits on the edge of a band and
        // must stay grabbable, and it is the smaller target of the two.
        zIndex: 5,
      }}
    >
      {panes.map((pane, i) => {
        const band = bands[i];
        if (!band) return null;
        return (
          <div
            key={pane.id}
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              top: `${band.y * 100}%`,
              height: `${band.height * 100}%`,
              // Inherited `none`, stated because it is the load-bearing part:
              // this band spans the whole pane, and one pixel of `auto` here is
              // a chart that cannot be panned.
              pointerEvents: "none",
              display: "flex",
              // **Top of the band, centred across it.** Centred vertically it
              // sat in the middle of the price action it is there to move, and
              // on a tall pane that is the busiest part of the picture. The top
              // strip is the quiet one — the value legend takes the top *left*
              // and the badges the top *right*, so the middle of that edge is
              // the gap between two things that already avoid each other.
              alignItems: "flex-start",
              justifyContent: "center",
              paddingTop: 4,
            }}
          >
            {hovered === pane.id && (
              <PaneToolbar
                name={paneName(panes, i)}
                canMoveUp={i > 0}
                canMoveDown={i < panes.length - 1}
                onMove={(delta) => onMove(pane.id, delta)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * What to call the pane in a screen-reader label.
 *
 * The role is all the model reliably carries — an indicator pane holds whatever
 * families were added to it, and naming it after the first would be wrong the
 * moment a second joined. "Indicator pane 2" is at least unambiguous, which two
 * toolbars both reading "Move pane up" would not be.
 */
function paneName(panes: readonly Pane[], i: number): string {
  const pane = panes[i]!;
  if (pane.role === "price") return "price pane";
  if (pane.role === "volume") return "volume pane";
  // Numbered among the indicator panes only, so the count matches what a reader
  // sees rather than counting price and volume along the way.
  const nth = panes.slice(0, i + 1).filter((p) => p.role === "indicator").length;
  return `indicator pane ${nth}`;
}

/**
 * The strip itself: one bordered group, two segments, a hairline between them.
 *
 * Quiet at rest and solid under the pointer. It sits on top of the price action
 * it is there to move, so it cannot be opaque chrome — but it cannot be a ghost
 * either, or the glyphs are unreadable on a light theme. 0.75 to 1 is the span
 * `FloatingButton` settled on after 0.45 failed contrast on White.
 */
function PaneToolbar({
  name,
  canMoveUp,
  canMoveDown,
  onMove,
}: {
  name: string;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMove: (delta: -1 | 1) => void;
}) {
  const [over, setOver] = useState(false);
  return (
    <div
      onPointerEnter={() => setOver(true)}
      onPointerLeave={() => setOver(false)}
      style={{
        display: "flex",
        alignItems: "stretch",
        // `hidden` is what puts the segments' square corners inside the group's
        // rounded ones, so the strip reads as one object rather than two.
        overflow: "hidden",
        borderRadius: 4,
        border: "1px solid var(--qf-border)",
        background: "var(--qf-surface)",
        opacity: over ? 1 : 0.75,
        transition: "opacity 120ms",
        // The group is `auto` only because its buttons are; the band around it
        // stays inert. Stated so a future child does not have to remember.
        pointerEvents: "auto",
      }}
    >
      <ToolbarButton
        glyph="▲"
        title="Move this pane up"
        label={`Move ${name} up`}
        disabled={!canMoveUp}
        onClick={() => onMove(-1)}
      />
      <ToolbarButton
        glyph="▼"
        title="Move this pane down"
        label={`Move ${name} down`}
        disabled={!canMoveDown}
        // The divider is the second button's own left border, so it appears once
        // between the segments and never on the outside of the group.
        divided
        onClick={() => onMove(1)}
      />
    </div>
  );
}

function ToolbarButton({
  glyph,
  title,
  label,
  disabled,
  divided,
  onClick,
}: {
  glyph: string;
  title: string;
  label: string;
  disabled?: boolean;
  divided?: boolean;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const lit = hovered && !disabled;
  return (
    <button
      type="button"
      title={title}
      aria-label={label}
      disabled={disabled}
      // Both, and for two different reasons: `pointerdown` is what
      // `ZoomPanModifier` starts a pan on, and `click` is what the surface's own
      // click handling would treat as a chart click. A disabled button fires
      // neither, which is why the guard is not repeated inside them.
      onPointerDown={(event: ReactPointerEvent<HTMLButtonElement>) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      style={{
        ...SEGMENT,
        borderLeft: divided ? "1px solid var(--qf-border)" : "none",
        background: lit ? "var(--qf-background)" : "transparent",
        color: lit ? "var(--qf-text)" : "var(--qf-text-secondary)",
        cursor: disabled ? "default" : "pointer",
        // Disabled fades further rather than disappearing — the strip keeps its
        // width at the ends of the stack, and a control nobody can see is one
        // nobody can ask about.
        opacity: disabled ? 0.3 : 1,
      }}
    >
      {glyph}
    </button>
  );
}

/**
 * How far down the pane the reorder strip reaches, in pixels.
 *
 * Exported because `AnnotationStyleBar` sits in the same place — top of the
 * pane, centred — and the two were drawn on top of each other. Sharing the
 * number rather than each holding its own is what stops them colliding again
 * the next time this strip changes size.
 *
 * `paddingTop` + the button box: `fontSize` 10 at `lineHeight` 1, `5px`
 * padding top and bottom, and the toolbar's own 1px border either side.
 */
export const PANE_TOOLBAR_BOTTOM = 4 + 10 + 5 + 5 + 2;

const SEGMENT: CSSProperties = {
  border: "none",
  font: "inherit",
  fontSize: 10,
  lineHeight: 1,
  padding: "5px 9px",
  transition: "background 120ms, color 120ms, opacity 120ms",
};
